import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { ISieveSetDimensions } from 'Data/ViewModels/Equipment';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import deepEquals from '../../../../Lib/Object/deepEquals';
import { Field, NumericInput, RadioGroup, TextOutput } from '../../../Core';
import FormattedText from '../../../Core/FormattedText';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import {
  SHAPE_CIRCULAR,
  SHAPE_RECTANGULAR,
  SIZE_OPTION_CUSTOM_CIRCULAR,
  SIZE_OPTION_CUSTOM_RECTANGULAR,
  getPredefinedLabelForSieveSetDimensions,
  getSieveSetSizeOptionValueForDimensions,
  getSieveSetSizeOptions,
} from '../../../Equipment/SieveSet/SieveSetHelper';
import { FieldSet } from '../../../Layout';

// Field set to select the dimensions of a sieve set
// It is its own separate component as the logic is fairly complex.
// Possibly too complex, but needs must as the devil drives.
let SieveSetDimensionsFieldSet: React.FC<IWorksheetComponent> = ({
  namePrefix = undefined,
  createWorksheetSelector = undefined,
  disabled = undefined,
}) => {
  const dispatch = useDispatch();

  const sieveSetDimensions: ISieveSetDimensions = useSelector(
    createWorksheetSelector!('SieveSetDimensions'),
    deepEquals
  );
  const units = useSelector(createWorksheetSelector!('SieveSetUnits'));

  const sieveSetSizeOptions = getSieveSetSizeOptions(units);

  const matchingSieveSizeOption = getSieveSetSizeOptionValueForDimensions(sieveSetDimensions, units);

  const [sieveSetSizeOption, setSieveSetSizeOption] = useState(matchingSieveSizeOption);

  const isSieveSetDimensionsImported = useSelector(
    pipe(createWorksheetSelector!('SieveSet.Dimensions'), hasNonEmptyValue)
  );

  const isSieveSetSyncPending = useSelector(
    pipe(createWorksheetSelector!('SieveSet'), (s) => hasValue(s) && !hasNonEmptyValue(s.Status))
  );

  // Hacky method to replace "in" uom with inch symbol
  const getUomSuffix = (uom: string) => (uom === 'in' ? '″' : ` ${uom}`);

  const sieveSetSizeDescription =
    // If there is a predefined label for these dimensions (i.e. it's a radio option), use that
    getPredefinedLabelForSieveSetDimensions(sieveSetDimensions, units) ??
    // Otherwise construct an appropriate description for its shape
    (sieveSetDimensions?.Shape === SHAPE_CIRCULAR && sieveSetDimensions.Diameter
      ? [
          <FormattedText name="SieveSetDimensions.Diameter" key={0} />,
          getUomSuffix(sieveSetDimensions.Diameter?.uom),
          ' Diameter',
        ]
      : sieveSetDimensions?.Shape === SHAPE_RECTANGULAR && sieveSetDimensions.Length && sieveSetDimensions.Width
      ? [
          <FormattedText name="SieveSetDimensions.Length" key={0} />,
          getUomSuffix(sieveSetDimensions.Length?.uom),
          ' by ',
          <FormattedText name="SieveSetDimensions.Width" key={1} />,
          getUomSuffix(sieveSetDimensions.Width?.uom),
        ]
      : 'Unknown');

  const changeSieveSetSizeOption = useCallback(
    (sieveSetSizeOption: any) => {
      if (disabled) return;
      let selectedOptionDimensions = sieveSetSizeOptions.find((o: any) => o.value === sieveSetSizeOption).dimensions;
      if (!selectedOptionDimensions) {
        throw new Error(`Could not obtain a default size for sieve size set option: ${sieveSetSizeOption}`);
      }

      dispatch(
        actions.setValue({
          name: `${namePrefix}.SieveSetDimensions`,
          value: selectedOptionDimensions,
          sync: true,
        })
      );

      setSieveSetSizeOption(sieveSetSizeOption);
    },
    [dispatch, namePrefix, disabled, sieveSetSizeOptions]
  );

  // Apply imported sieve set size option to local state
  useEffect(() => {
    if (isSieveSetDimensionsImported && sieveSetSizeOption !== matchingSieveSizeOption)
      setSieveSetSizeOption(matchingSieveSizeOption);
  }, [sieveSetSizeOption, matchingSieveSizeOption, isSieveSetDimensionsImported, changeSieveSetSizeOption]);

  if (isSieveSetSyncPending || isSieveSetDimensionsImported)
    return (
      <Field
        input={<TextOutput />}
        label="Sieve Set Size"
        value={sieveSetSizeDescription}
        loading={isSieveSetSyncPending}
      />
    );

  return (
    <FieldSet>
      <Field
        input={<RadioGroup />}
        label="Sieve Set Size"
        options={getSieveSetSizeOptions(units)}
        value={sieveSetSizeOption}
        onChange={(e: any) => changeSieveSetSizeOption(e.data.value)}
      />

      <Field
        input={<NumericInput />}
        label="Sieve Diameter"
        name="SieveSetDimensions.Diameter"
        visible={sieveSetSizeOption === SIZE_OPTION_CUSTOM_CIRCULAR}
      />

      <Field
        input={<NumericInput />}
        label="Sieve Length"
        name="SieveSetDimensions.Length"
        visible={sieveSetSizeOption === SIZE_OPTION_CUSTOM_RECTANGULAR}
      />

      <Field
        input={<NumericInput />}
        label="Sieve Width"
        name="SieveSetDimensions.Width"
        visible={sieveSetSizeOption === SIZE_OPTION_CUSTOM_RECTANGULAR}
      />
    </FieldSet>
  );
};

SieveSetDimensionsFieldSet = withInjectedProps(SieveSetDimensionsFieldSet);

export { SieveSetDimensionsFieldSet };
